import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import AdminShell from '../components/admin/AdminShell';
import {
  buildAppRideAssignPayload,
  buildAppRideStatusPayload,
  buildWebRideAssignPayload,
  fetchRideBySource,
  formatRideDateParts,
  formatRideDistance,
  formatRideFare,
  formatRideFareInrHint,
  getRideDisplayMarket,
  formatRidePhone,
  mapAppRide,
  mapWebRide,
  notifyAssignedAppDriver,
  notifyRiderOfAdminAppStatusChange,
  rideStatusLabel,
} from '../lib/rides';
import { assignableDriverLabel, fetchAssignableDrivers } from '../lib/assignable-drivers';
import {
  filterDriversForRide,
  resolveDriverSelectId,
} from '../lib/driver-ride-services';
import { isDailyProductRide, rideProductTabLabel } from '../lib/ride-products';
import { isSupabaseConfigured, supabase } from '../lib/supabase';

function AdminRideDetailPage() {
  const { source, id } = useParams();
  const navigate = useNavigate();
  const [sessionChecked, setSessionChecked] = useState(false);
  const [user, setUser] = useState(null);
  const [ride, setRide] = useState(null);
  const [approvedDrivers, setApprovedDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [driverId, setDriverId] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const rideSource = source === 'web' ? 'web' : 'app';
  const rideId = decodeURIComponent(id || '');

  useEffect(() => {
    if (!isSupabaseConfigured || !supabase) {
      setSessionChecked(true);
      return undefined;
    }

    let active = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!active) return;
      const nextUser = data.session?.user || null;
      setUser(nextUser);
      setSessionChecked(true);
      if (!nextUser) navigate('/admin/login', { replace: true });
    });

    const { data: authListener } = supabase.auth.onAuthStateChange((_event, session) => {
      const nextUser = session?.user || null;
      setUser(nextUser);
      if (!nextUser) navigate('/admin/login', { replace: true });
    });

    return () => {
      active = false;
      authListener.subscription.unsubscribe();
    };
  }, [navigate]);

  const loadDrivers = useCallback(async () => {
    if (!supabase) return;
    const { drivers, error: driversError } = await fetchAssignableDrivers(supabase);
    setApprovedDrivers(drivers);
    if (driversError) {
      console.warn('[admin] assignable drivers:', driversError);
    }
  }, []);

  const loadRide = useCallback(async () => {
    if (!supabase || !rideId) return;
    setLoading(true);
    setError('');
    const { ride: nextRide, error: loadError } = await fetchRideBySource(
      supabase,
      rideSource,
      rideId
    );
    setRide(nextRide);
    setAdminNotes(nextRide?.notes || '');
    setDriverId(
      resolveDriverSelectId(
        approvedDrivers,
        nextRide?.assigned_driver_id || ''
      )
    );
    setError(loadError || (nextRide ? '' : 'Ride not found.'));
    setLoading(false);
  }, [rideId, rideSource]);

  useEffect(() => {
    if (user) loadRide();
  }, [user, loadRide]);

  useEffect(() => {
    if (!supabase || !user || !rideId) return undefined;

    const table = rideSource === 'web' ? 'rides' : 'ride_requests';
    const channel = supabase
      .channel(`admin-ride-detail-${rideSource}-${rideId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table,
          filter: `id=eq.${rideId}`,
        },
        (payload) => {
          if (!payload.new) return;
          const mapped =
            rideSource === 'web' ? mapWebRide(payload.new) : mapAppRide(payload.new);
          setRide(mapped);
          setAdminNotes(mapped.notes || '');
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, rideId, rideSource]);

  useEffect(() => {
    if (!user || !ride) return;
    if (ride.source === 'app' && isDailyProductRide(ride)) return;
    loadDrivers();
  }, [user, ride, loadDrivers]);

  useEffect(() => {
    if (!ride?.assigned_driver_id) return;
    setDriverId(resolveDriverSelectId(approvedDrivers, ride.assigned_driver_id));
  }, [ride?.assigned_driver_id, ride?.id, approvedDrivers]);

  const assignableDrivers = useMemo(
    () =>
      filterDriversForRide(approvedDrivers, ride, {
        keepDriverId: ride?.assigned_driver_id,
      }),
    [approvedDrivers, ride]
  );

  const driverById = useMemo(() => {
    const map = new Map();
    approvedDrivers.forEach((driver) => map.set(driver.id, driver));
    return map;
  }, [approvedDrivers]);

  const detailMeta = useMemo(() => {
    if (!ride) return null;
    const when = formatRideDateParts(ride.scheduled_at || ride.created_at);
    const driverSummary = ride.assigned_driver_name
      ? `${ride.assigned_driver_name}${
          ride.assigned_vehicle ? ` · ${ride.assigned_vehicle}` : ''
        }`
      : ride.assigned_driver_id
        ? 'Driver assigned'
        : 'Not assigned yet';
    return { when, driverSummary };
  }, [ride]);

  const updateRide = async (payload) => {
    if (!ride || !supabase) return false;
    setSaving(true);
    setError('');

    const table = ride.source === 'app' ? 'ride_requests' : 'rides';
    const { data, error: updateError } = await supabase
      .from(table)
      .update(payload)
      .eq('id', ride.id)
      .select('*')
      .maybeSingle();

    setSaving(false);

    if (updateError) {
      const message = updateError.message || 'Update failed';
      const code = updateError.code ? ` (${updateError.code})` : '';
      const isRls = /row-level security|permission|42501|PGRST301/i.test(
        `${message} ${updateError.code || ''}`
      );
      setError(
        isRls
          ? `${message}${code}. Your login still is not allowed to update rides. Run the FULL supabase/fix-ride-board-app-bookings.sql in Supabase, wait 10 seconds, then sign out and sign in again.`
          : `${message}${code}`
      );
      return false;
    }

    if (!data) {
      setError(
        'Update returned no row. Run supabase/fix-ride-board-app-bookings.sql, then sign out/in.'
      );
      return false;
    }

    const mapped = ride.source === 'app' ? mapAppRide(data) : mapWebRide(data);
    setRide(mapped);
    setAdminNotes(mapped.notes || '');
    setDriverId(
      resolveDriverSelectId(approvedDrivers, mapped.assigned_driver_id || '')
    );
    return true;
  };

  const assignDriver = async () => {
    if (!driverId) {
      setError('Select an approved driver to assign.');
      return;
    }
    const driver = driverById.get(driverId);
    if (!driver) {
      setError('Selected driver was not found.');
      return;
    }

    if (ride?.source === 'app') {
      const payload = await buildAppRideAssignPayload(
        supabase,
        ride,
        driver,
        adminNotes.trim() || null
      );
      const prevBoard = ride.boardStatus;
      const ok = await updateRide(payload);
      if (ok) {
        await notifyRiderOfAdminAppStatusChange(supabase, ride, 'assigned', prevBoard);
        await notifyAssignedAppDriver(supabase, ride, driver);
      }
      return;
    }

    const ok = await updateRide(buildWebRideAssignPayload(driver, adminNotes.trim() || null));
    if (ok) {
      await notifyAssignedAppDriver(supabase, { ...ride, source: 'web' }, driver);
    }
  };

  const saveNotes = async () => {
    if (ride?.source === 'app') {
      await updateRide({ admin_note: adminNotes.trim() || null });
      return;
    }
    await updateRide({ admin_notes: adminNotes.trim() || null });
  };

  const setRideStatus = async (boardStatus) => {
    if (!ride) return;

    if (ride.source === 'app') {
      const hiringDriver =
        boardStatus === 'assigned' && driverId ? driverById.get(driverId) : null;
      let payload = buildAppRideStatusPayload(
        boardStatus,
        ride,
        adminNotes.trim() || null,
        hiringDriver
      );

      if (boardStatus === 'assigned' && hiringDriver) {
        payload = {
          ...payload,
          ...(await buildAppRideAssignPayload(
            supabase,
            ride,
            hiringDriver,
            adminNotes.trim() || null
          )),
        };
      }

      const prevBoard = ride.boardStatus;
      const ok = await updateRide(payload);
      if (ok) {
        await notifyRiderOfAdminAppStatusChange(supabase, ride, boardStatus, prevBoard);
        if (boardStatus === 'assigned' && hiringDriver) {
          await notifyAssignedAppDriver(supabase, ride, hiringDriver);
        }
      }
      return;
    }

    const payload = {
      status:
        boardStatus === 'open'
          ? 'pending'
          : boardStatus === 'live'
            ? 'in_progress'
            : boardStatus,
      admin_notes: adminNotes.trim() || null,
    };
    if (boardStatus === 'open') {
      payload.assigned_driver_id = null;
      payload.assigned_at = null;
      setDriverId('');
    }
    await updateRide(payload);
  };

  const logout = async () => {
    await supabase?.auth.signOut();
    navigate('/admin/login', { replace: true });
  };

  if (!sessionChecked) {
    return <div className="admin-loading">Checking admin session…</div>;
  }

  if (!user) return null;

  const isInstantRide = Boolean(ride && ride.source === 'app' && isDailyProductRide(ride));
  const listPath = isInstantRide ? '/admin/instant-rides' : '/admin/hire-rides';
  const listLabel = isInstantRide ? 'Back to instant rides' : 'Back to hire a car trips';
  const appStatusLabel =
    ride?.status &&
    String(ride.status)
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <AdminShell
      userEmail={user.email}
      active={isInstantRide ? 'instant' : 'hire'}
      onLogout={logout}
    >
      <div className="ride-detail-page">
        <div className="ride-detail-page__nav">
          <Link to={listPath} className="ride-detail-page__back">
            ← {listLabel}
          </Link>
          <button type="button" className="btn btn--ghost-dark" onClick={loadRide} disabled={loading}>
            {loading ? 'Refreshing…' : 'Refresh trip'}
          </button>
        </div>

        {error && <div className="form-alert form-alert--error admin-error">{error}</div>}

        {loading && !ride ? (
          <p className="admin-empty ride-detail-page__empty">Loading trip details…</p>
        ) : !ride ? (
          <p className="admin-empty ride-detail-page__empty">Trip not found.</p>
        ) : (
          <article className={`trip-detail trip-detail--easy trip-detail--${ride.boardStatus}`}>
            <header className="trip-detail__head">
              <div className="trip-detail__identity">
                <p className="trip-detail__kicker">Trip #{ride.id}</p>
                <h1>{ride.customer_name}</h1>
                <p className="trip-detail__phone">
                  {formatRidePhone(ride.customer_phone, getRideDisplayMarket(ride))}
                </p>
                <div className="trip-detail__tags">
                  <span className={`ride-pill ride-pill--${ride.boardStatus}`}>
                    {rideStatusLabel(ride)}
                  </span>
                  <span className={`ride-source ride-source--${ride.source}`}>
                    {isInstantRide ? 'Instant ride · App' : ride.source === 'app' ? 'App booking' : 'Website'}
                  </span>
                  {!isInstantRide ? (
                    <span className="trip-detail__product-tag">{rideProductTabLabel(ride)}</span>
                  ) : null}
                </div>
              </div>
              <ul className="trip-detail__summary" aria-label="Trip summary">
                <li className="trip-detail__summary-item">
                  <span>Fare</span>
                  <strong>{formatRideFare(ride)}</strong>
                </li>
                <li className="trip-detail__summary-item">
                  <span>Distance</span>
                  <strong>{formatRideDistance(ride.distance_km, getRideDisplayMarket(ride))}</strong>
                </li>
                <li className="trip-detail__summary-item">
                  <span>Scheduled pickup</span>
                  <strong>
                    {detailMeta?.when.date}
                    {detailMeta?.when.time ? ` · ${detailMeta.when.time}` : ''}
                  </strong>
                </li>
                <li className="trip-detail__summary-item">
                  <span>Driver</span>
                  <strong>{detailMeta?.driverSummary}</strong>
                </li>
              </ul>
            </header>

            <div
              className={`trip-detail__workspace ${
                isInstantRide ? 'trip-detail__workspace--instant' : ''
              }`}
            >
              <section className="trip-detail__panel">
                <h2 className="trip-detail__section-title">Route</h2>
                <div className="trip-detail__route">
                  <div className="trip-detail__route-stop">
                    <span>Pickup</span>
                    <p>{ride.pickup_address || '—'}</p>
                  </div>
                  <div className="trip-detail__route-connector" aria-hidden="true" />
                  <div className="trip-detail__route-stop trip-detail__route-stop--drop">
                    <span>Drop off</span>
                    <p>{ride.dropoff_address || '—'}</p>
                  </div>
                </div>

                {!isInstantRide ? (
                  <div className="trip-detail__status-panel">
                    <h2 className="trip-detail__section-title">Trip status</h2>
                    <p className="trip-detail__status-current">
                      Current status{' '}
                      <span className={`ride-pill ride-pill--${ride.boardStatus}`}>
                        {rideStatusLabel(ride)}
                      </span>
                    </p>
                    <p className="trip-detail__status-help">Tap a button to change status:</p>
                    <div className="trip-detail__status-grid">
                      <button
                        type="button"
                        className={`trip-detail__status-btn trip-detail__status-btn--open${
                          ride.boardStatus === 'open' ? ' trip-detail__status-btn--current' : ''
                        }`}
                        disabled={saving}
                        onClick={() => setRideStatus('open')}
                      >
                        Open request
                      </button>
                      <button
                        type="button"
                        className={`trip-detail__status-btn trip-detail__status-btn--assigned${
                          ride.boardStatus === 'assigned' ? ' trip-detail__status-btn--current' : ''
                        }`}
                        disabled={saving}
                        onClick={() => setRideStatus('assigned')}
                      >
                        Driver assigned
                      </button>
                      <button
                        type="button"
                        className={`trip-detail__status-btn trip-detail__status-btn--live${
                          ride.boardStatus === 'live' ? ' trip-detail__status-btn--current' : ''
                        }`}
                        disabled={saving}
                        onClick={() => setRideStatus('live')}
                      >
                        In trip
                      </button>
                      <button
                        type="button"
                        className={`trip-detail__status-btn trip-detail__status-btn--completed${
                          ride.boardStatus === 'completed' ? ' trip-detail__status-btn--current' : ''
                        }`}
                        disabled={saving}
                        onClick={() => setRideStatus('completed')}
                      >
                        Completed
                      </button>
                      <button
                        type="button"
                        className={`trip-detail__status-btn trip-detail__status-btn--cancelled${
                          ride.boardStatus === 'cancelled' ? ' trip-detail__status-btn--current' : ''
                        }`}
                        disabled={saving}
                        onClick={() => setRideStatus('cancelled')}
                      >
                        Cancelled
                      </button>
                      <button
                        type="button"
                        className="trip-detail__status-btn trip-detail__status-btn--unassign"
                        disabled={saving || ride.boardStatus === 'open'}
                        onClick={() => setRideStatus('open')}
                      >
                        Unassign driver
                      </button>
                    </div>
                  </div>
                ) : null}

                <h2 className="trip-detail__section-title">Booking details</h2>
                <dl className="trip-detail__facts trip-detail__facts--booking">
                  <div>
                    <dt>Vehicle</dt>
                    <dd>{ride.vehicle_type || '—'}</dd>
                  </div>
                  <div>
                    <dt>Payment</dt>
                    <dd>{ride.payment || '—'}</dd>
                  </div>
                  <div>
                    <dt>Currency</dt>
                    <dd>
                      {ride.source === 'app'
                        ? getRideDisplayMarket(ride) === 'USD'
                          ? 'USD ($) · fare stored in INR'
                          : 'INR (₹)'
                        : getRideDisplayMarket(ride) === 'USD'
                          ? 'USD ($)'
                          : 'INR (₹)'}
                    </dd>
                  </div>
                  {isInstantRide ? (
                    <div>
                      <dt>App status</dt>
                      <dd>{appStatusLabel || '—'}</dd>
                    </div>
                  ) : null}
                  {formatRideFareInrHint(ride) ? (
                    <div className="trip-detail__fact-wide">
                      <dt>Stored fare (INR)</dt>
                      <dd>{formatRideFareInrHint(ride)}</dd>
                    </div>
                  ) : null}
                </dl>
              </section>

              {isInstantRide ? (
                <aside className="trip-detail__dispatch trip-detail__dispatch--readonly">
                  <div className="trip-detail__actions-card">
                    <h2 className="trip-detail__actions-title">Instant ride — view only</h2>
                    <p className="trip-detail__actions-lead">
                      The Saarthi app assigns drivers and updates status automatically. You can
                      review details here; no admin changes are needed.
                    </p>
                    <ul className="trip-detail__checklist">
                      <li>
                        <strong>Status:</strong>{' '}
                        <span className={`ride-pill ride-pill--${ride.boardStatus}`}>
                          {rideStatusLabel(ride)}
                        </span>
                      </li>
                      <li>
                        <strong>Driver:</strong> {detailMeta?.driverSummary}
                      </li>
                      {ride.assigned_vehicle ? (
                        <li>
                          <strong>Vehicle:</strong> {ride.assigned_vehicle}
                        </li>
                      ) : null}
                      {adminNotes ? (
                        <li>
                          <strong>Admin note:</strong> {adminNotes}
                        </li>
                      ) : null}
                    </ul>
                  </div>
                </aside>
              ) : (
                <aside className="trip-detail__dispatch">
                  <div className="trip-detail__actions-card">
                    <h2 className="trip-detail__actions-title">Manage this trip</h2>
                    <p className="trip-detail__actions-lead">
                      Assign a driver and save internal notes here. Update trip status on the left
                      under <strong>Trip status</strong>.
                      {ride.source === 'web' ? (
                        <> Only drivers approved for outstation trips are listed.</>
                      ) : null}
                    </p>

                    <div className="trip-detail__step">
                      <h3 className="trip-detail__step-title">
                        <span className="trip-detail__step-num">1</span>
                        Assign a driver
                      </h3>
                      <label className="trip-detail__field">
                        <span>Select driver</span>
                        <select
                          value={driverId}
                          onChange={(e) => setDriverId(e.target.value)}
                          disabled={saving || !assignableDrivers.length}
                        >
                          <option value="">
                            {assignableDrivers.length
                              ? 'Choose a driver from the list…'
                              : ride?.source === 'web'
                                ? 'No outstation drivers — approve drivers with Outstation or Both'
                                : 'No matching drivers for this trip type'}
                          </option>
                          {assignableDrivers.some((d) => d.origin === 'web') ? (
                            <optgroup label="Website applications">
                              {assignableDrivers
                                .filter((d) => d.origin === 'web')
                                .map((driver) => (
                                  <option key={driver.id} value={driver.id}>
                                    {assignableDriverLabel(driver)}
                                  </option>
                                ))}
                            </optgroup>
                          ) : null}
                          {assignableDrivers.some((d) => d.origin === 'app') ? (
                            <optgroup label="Saarthi app drivers">
                              {assignableDrivers
                                .filter((d) => d.origin === 'app')
                                .map((driver) => (
                                  <option key={driver.id} value={driver.id}>
                                    {assignableDriverLabel(driver)}
                                  </option>
                                ))}
                            </optgroup>
                          ) : null}
                        </select>
                      </label>
                      <button
                        type="button"
                        className="btn btn--primary trip-detail__step-btn"
                        onClick={assignDriver}
                        disabled={saving || !driverId}
                      >
                        {saving ? 'Saving…' : 'Assign driver'}
                      </button>
                    </div>

                    <div className="trip-detail__step">
                      <h3 className="trip-detail__step-title">
                        <span className="trip-detail__step-num">2</span>
                        Admin notes
                      </h3>
                      <label className="trip-detail__field">
                        <span>Internal notes (optional)</span>
                        <textarea
                          rows={3}
                          value={adminNotes}
                          onChange={(e) => setAdminNotes(e.target.value)}
                          placeholder="Notes for your team — not visible to the rider"
                          disabled={saving}
                        />
                      </label>
                      <button
                        type="button"
                        className="btn btn--ghost-dark trip-detail__step-btn"
                        onClick={saveNotes}
                        disabled={saving}
                      >
                        Save notes
                      </button>
                    </div>
                  </div>
                </aside>
              )}
            </div>
          </article>
        )}
      </div>
    </AdminShell>
  );
}

export default AdminRideDetailPage;
